import { Businesshours } from '@gql/base-types';
import {
  DAY_NAMES,
  timeToDbFormat,
  dbTimeTodayjs,
  formatTime,
  formatTimeRange,
  validateTimeRange,
  sortBusinessHours,
} from '@features/settings/utils/businessHoursUtils';

// Re-export commonly used utilities
export { DAY_NAMES, timeToDbFormat, dbTimeTodayjs, formatTime, formatTimeRange, validateTimeRange, sortBusinessHours };

export type DriverTimeBlock = {
  id: string;
  openTime: string | null;
  closedTime: string | null;
  isDefault: boolean;
};

export type ProcessedDriverSchedule = {
  dayOfWeek: number;
  dayName: string;
  timeBlocks: DriverTimeBlock[];
  isOpen: boolean;
  type: string;
};

export type ProcessedDriverOneOffDay = {
  id: string;
  name: string;
  date: string; // Formatted date string
  dateObj: Date; // For sorting
  allDay: boolean;
  isWorking: boolean;
  openTime: string | null;
  closedTime: string | null;
};

/**
 * Process and merge driver schedule with defaults
 * Returns hours grouped by day with multiple time blocks per day
 */
export const processDriverSchedule = (
  schedules: Businesshours[],
  driverId: string
): ProcessedDriverSchedule[] => {
  // Filter driver-specific schedules
  // Note: Driver schedules use type='business-hours' - differentiated by driver_id
  const driverSpecific = schedules.filter(
    (h) => h.driver_id?.toString() === driverId && h.type === 'business-hours'
  );

  // Filter default driver hours (fallback)
  const defaults = schedules.filter(
    (h) => !h.driver_id && h.type === 'driver-default-hours'
  );

  const processed: ProcessedDriverSchedule[] = [];

  // Process each day of the week
  for (let day = 0; day <= 6; day++) {
    // Check for driver-specific hours first
    const driverHours = driverSpecific.filter((h) => h.day_of_week === day);

    if (driverHours.length > 0) {
      // Use driver-specific hours - multiple blocks possible
      const sortedHours = sortBusinessHours(driverHours);
      const timeBlocks: DriverTimeBlock[] = sortedHours.map((h) => ({
        id: h.id.toString(),
        openTime: h.open_time || null,
        closedTime: h.closed_time || null,
        isDefault: false,
      }));

      const hasOpenBlock = sortedHours.some((h) => h.is_open);

      processed.push({
        dayOfWeek: day,
        dayName: DAY_NAMES[day],
        timeBlocks,
        isOpen: hasOpenBlock,
        type: 'business-hours',
      });
    } else {
      // Fall back to default hours
      const defaultHours = defaults.filter((h) => h.day_of_week === day);

      if (defaultHours.length > 0) {
        const sortedHours = sortBusinessHours(defaultHours);
        const timeBlocks: DriverTimeBlock[] = sortedHours.map((h) => ({
          id: h.id.toString(),
          openTime: h.open_time || null,
          closedTime: h.closed_time || null,
          isDefault: true,
        }));

        const hasOpenBlock = sortedHours.some((h) => h.is_open);

        processed.push({
          dayOfWeek: day,
          dayName: DAY_NAMES[day],
          timeBlocks,
          isOpen: hasOpenBlock,
          type: 'driver-default-hours',
        });
      } else {
        // No hours defined for this day (treat as closed/unavailable)
        processed.push({
          dayOfWeek: day,
          dayName: DAY_NAMES[day],
          timeBlocks: [],
          isOpen: false,
          type: 'driver-default-hours',
        });
      }
    }
  }

  return processed;
};

/**
 * Process driver one-off days for display
 * Returns one-off days sorted by date
 */
export const processDriverOneOffDays = (
  schedules: Businesshours[],
  driverId: string
): ProcessedDriverOneOffDay[] => {
  const oneOffDays = schedules.filter(
    (h) => h.driver_id?.toString() === driverId && h.type === 'one-off' && h.day && h.month && h.year
  );

  return oneOffDays
    .map((entry) => {
      const dateObj = new Date(entry.year!, entry.month! - 1, entry.day!);
      return {
        id: entry.id.toString(),
        name: entry.name,
        date: dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        dateObj: dateObj,
        allDay: !entry.is_open && !entry.open_time,
        isWorking: !!entry.is_open,
        openTime: entry.open_time,
        closedTime: entry.closed_time,
      };
    })
    .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
};
